/**
 * PageHelp — guida onboarding collassabile mostrata in fondo a ogni pagina.
 *
 * - Renderizzata una sola volta nel Layout (sotto l'Outlet): appare in coda al contenuto.
 * - Il contenuto è scelto in base alla route corrente (mappa pattern→pageId + PAGE_HELP).
 * - Aperta di default; lo stato apri/chiudi è una preferenza utente PER-PAGINA salvata in
 *   localStorage (chiave `tc:pagehelp:<pageId>`), così chiudere la guida su una pagina non la
 *   nasconde sulle altre.
 * - Su route senza contenuto (login/callback o pagine non mappate) non renderizza nulla.
 */

import { useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { PAGE_HELP, type PageHelpContent } from './pageHelp.content.js';
import styles from './PageHelp.module.css';

/** Mappa pattern di route → pageId (chiave in PAGE_HELP). matchPath è esatto di default. */
const ROUTE_TO_PAGE: { pattern: string; id: string }[] = [
  { pattern: '/', id: 'dashboard' },
  { pattern: '/kev', id: 'kev' },
  { pattern: '/products', id: 'products-list' },
  { pattern: '/products/:id', id: 'product-detail' },
  { pattern: '/tickets', id: 'triage' },
  { pattern: '/tickets/:projectUuid', id: 'triage' },
  { pattern: '/enisa-reports', id: 'enisa-reports' },
  { pattern: '/enisa-reports/:id', id: 'enisa-report-detail' },
  { pattern: '/admin', id: 'admin' },
  // Slice A — Anagrafica clienti (Forniture)
  { pattern: '/customers', id: 'clienti' },
  { pattern: '/customers/:id', id: 'clienti-detail' },
  // Portale job Prefect
  { pattern: '/jobs', id: 'prefect-jobs' },
  // Batch Edit API
  { pattern: '/batch-edits', id: 'batch-edits' },
  { pattern: '/batch-edits/:batchId', id: 'batch-edit-detail' },
  // Log Audit (VIEW_AUDIT_LOG)
  { pattern: '/audit', id: 'audit-log' },
];

const storageKey = (pageId: string): string => `tc:pagehelp:${pageId}`;

/** Pannello effettivo: gestisce lo stato apri/chiudi persistito per la singola pagina. */
function PageHelpPanel({ pageId, content }: { pageId: string; content: PageHelpContent }) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      // Default aperto: solo il valore esplicito 'closed' tiene chiuso.
      return localStorage.getItem(storageKey(pageId)) !== 'closed';
    } catch {
      return true;
    }
  });

  function toggle() {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey(pageId), next ? 'open' : 'closed');
      } catch {
        // localStorage non disponibile (es. modalità privata): stato solo in memoria.
      }
      return next;
    });
  }

  const bodyId = `page-help-body-${pageId}`;

  return (
    <section className={styles.help} data-testid="page-help" data-page-help-id={pageId}>
      <button
        type="button"
        className={styles.toggle}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={bodyId}
        data-testid="page-help-toggle"
      >
        <span className={styles.chevron} aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span>Guida pagina: {content.title}</span>
        <span className={styles.hint}>{open ? 'nascondi' : 'mostra'}</span>
      </button>

      {open && (
        <div id={bodyId} className={styles.body} data-testid="page-help-body">
          <p className={styles.what}>{content.what}</p>

          <h4 className={styles.sectionTitle}>Scenari d’uso</h4>
          <ul className={styles.list}>
            {content.scenarios.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>

          <h4 className={styles.sectionTitle}>Collegamenti con le altre pagine</h4>
          <ul className={styles.list}>
            {content.links.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** Sceglie il contenuto in base alla route e rende il pannello (remount per-pagina via key). */
export function PageHelp() {
  const { pathname } = useLocation();
  const match = ROUTE_TO_PAGE.find((r) => matchPath(r.pattern, pathname));
  if (!match) return null;
  const content = PAGE_HELP[match.id];
  if (!content) return null;

  // key={match.id}: cambiando pagina il pannello si rimonta e ri-legge la preferenza da localStorage.
  return <PageHelpPanel key={match.id} pageId={match.id} content={content} />;
}
